import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowsClockwise, CheckCircle, WarningOctagon } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useI18n } from '@/i18n'
import { formatGrams } from '@/lib/weight'
import { useSyncStatus } from '@/offline'
import {
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  IntegerInput,
  KeyValue,
  Skeleton,
  useToast,
} from '@/ui'
import { ActionBar, GateScreen } from '../components/GateScreen'
import { closeDay } from '../data/actions'
import { useGate } from '../data/useGate'
import type { GateSession } from '../data/session'
import { closeResult, reconcile } from '../model/day'
import { BiText } from '../components/GateScreen'

/** F4.11 — दिन बंद करें · Close the day: hand counts, local reconciliation preview, close_session. */
export function CloseDayPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const g = useGate()
  if (!g.ready) return <Skeleton className="h-64 w-full" />
  const lastClosed = g.sessions
    .filter((s) => s.status === 'CLOSED' && s.capture_mode === 'APP')
    .sort((a, b) => ((a.closed_at ?? '') < (b.closed_at ?? '') ? 1 : -1))[0]
  return (
    <GateScreen title="gate.closeDay">
      {g.app ? (
        <CloseDayForm session={g.app} />
      ) : lastClosed ? (
        <ClosedResult session={lastClosed} />
      ) : (
        <EmptyState
          title={t('gate.day.noneOpen')}
          action={<Button onClick={() => navigate('/m/gate')}>{t('gate.day.title')}</Button>}
        />
      )}
    </GateScreen>
  )
}

export function CloseDayForm({
  session,
  slipRange,
  blockers = [],
}: {
  session: GateSession
  slipRange?: { book_id: string; first_slip_no: string; last_slip_no: string } | null
  blockers?: string[]
}) {
  const { t } = useI18n()
  const toast = useToast()
  const actor = useActor()
  const navigate = useNavigate()
  const g = useGate()
  const sync = useSyncStatus()
  const rec = useMemo(() => reconcile(g.outbox, session.id), [g.outbox, session.id])
  const [tags, setTags] = useState<number | null>(null)
  const [seals, setSeals] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const draft = g.drafts.find((x) => x.data.session_id === session.id)
  const tagsOk = tags !== null && tags === rec.tags.shouldBeLeft
  const sealsOk = seals !== null && seals === rec.seals.shouldBeLeft
  const problems = [
    ...(draft ? [t('gate.closeDay.draftOpen', { name: draft.data.farmer.name })] : []),
    ...blockers,
  ]
  const canClose = problems.length === 0 && tags !== null && seals !== null

  const balanceLine = (ok: boolean, typed: number | null) =>
    typed === null ? null : ok ? (
      <Banner tone="success">{t('gate.closeDay.balanced')}</Banner>
    ) : (
      <Banner tone="warn">{t('gate.closeDay.notBalanced')}</Banner>
    )

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader eyebrow={t('gate.closeDay.lotsToday')} />
        <CardBody className="py-2">
          <KeyValue
            items={[
              {
                label: t('gate.lot.good'),
                value: t('gate.closeDay.lotsLine', {
                  lots: rec.lotsMain,
                  bags: rec.bagsMain,
                  kg: formatGrams(rec.netMainG),
                }),
              },
              {
                label: t('gate.lot.red'),
                value: t('gate.closeDay.lotsLine', {
                  lots: rec.lotsConv,
                  bags: rec.bagsConv,
                  kg: formatGrams(rec.netConvG),
                }),
              },
            ]}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader eyebrow={t('gate.closeDay.tagCount')} />
        <CardBody className="flex flex-col gap-3">
          <CountGrid
            open={rec.tags.atOpen}
            confirmed={rec.tags.confirmedToday}
            bound={rec.tags.bound}
            voided={rec.tags.voided}
            left={rec.tags.shouldBeLeft}
          />
          <Field label={t('gate.closeDay.tagsCounted')} required>
            <IntegerInput size="touch" maxDigits={4} value={tags} onChange={setTags} />
          </Field>
          {balanceLine(tagsOk, tags)}
        </CardBody>
      </Card>

      <Card>
        <CardHeader eyebrow={t('gate.closeDay.sealCount')} />
        <CardBody className="flex flex-col gap-3">
          <CountGrid
            open={rec.seals.atOpen}
            confirmed={rec.seals.confirmedToday}
            bound={rec.seals.bound}
            voided={rec.seals.voided}
            left={rec.seals.shouldBeLeft}
          />
          <Field label={t('gate.closeDay.sealsCounted')} required>
            <IntegerInput size="touch" maxDigits={4} value={seals} onChange={setSeals} />
          </Field>
          {balanceLine(sealsOk, seals)}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex items-center justify-between gap-3 py-3">
          <div className="text-[14px]">
            <div>{t('gate.closeDay.recordsWaiting', { count: sync.pending })}</div>
            <div className="text-muted">
              {t('gate.closeDay.photosWaiting', { count: sync.mediaPending })}
            </div>
          </div>
          <Button
            size="touch"
            variant="secondary"
            leadingIcon={<ArrowsClockwise aria-hidden="true" />}
            loading={sync.phase === 'syncing'}
            onClick={() => void sync.syncNow()}
          >
            {t('sync.now')}
          </Button>
        </CardBody>
      </Card>

      <ActionBar>
        {problems[0] && (
          <p className="text-center text-[13px] font-semibold text-danger">{problems[0]}</p>
        )}
        <Button
          size="xl"
          fullWidth
          disabled={!canClose}
          loading={busy}
          onClick={async () => {
            if (tags === null || seals === null) return
            setBusy(true)
            try {
              await closeDay(session, actor.user_id, {
                tags_counted: tags,
                seals_counted: seals,
                lots_main: rec.lotsMain,
                lots_conv: rec.lotsConv,
                slip_range: slipRange ?? null,
                closed_at: session.paper?.day_closed_at ?? undefined,
              })
              toast.success(t('gate.closeDay.closed'))
              void sync.syncNow()
              navigate(session.capture_mode === 'PAPER' ? '/m/gate' : '/m/gate/close-day', {
                replace: true,
              })
            } catch (e) {
              toast.error(t('gate.saveFailed'), e instanceof Error ? e.message : String(e))
            } finally {
              setBusy(false)
            }
          }}
        >
          <BiText k="gate.closeDay.close" />
        </Button>
      </ActionBar>
    </div>
  )
}

function CountGrid({
  open,
  confirmed,
  bound,
  voided,
  left,
}: {
  open: number
  confirmed: number
  bound: number
  voided: number
  left: number
}) {
  const { t } = useI18n()
  const cells = [
    {
      k: t('gate.closeDay.inHand'),
      v: open + confirmed,
      hint: confirmed ? t('gate.closeDay.plusOpened', { n: confirmed }) : undefined,
    },
    { k: t('gate.closeDay.onBags'), v: bound },
    { k: t('gate.closeDay.voided'), v: voided },
    { k: t('gate.closeDay.shouldBeLeft'), v: left },
  ]
  return (
    <div className="grid grid-cols-4 gap-1.5 text-center">
      {cells.map((c) => (
        <div key={c.k} className="rounded-chip bg-paper-sunk px-1 py-2">
          <div className="font-mono text-[20px] tabular">{c.v}</div>
          <div className="text-[11px] leading-tight text-muted">{c.k}</div>
          {c.hint && <div className="text-[10px] text-muted">{c.hint}</div>}
        </div>
      ))}
    </div>
  )
}

/** After Close day: sync state and, once the server answered, balanced or blocked (F4.11). */
function ClosedResult({ session }: { session: GateSession }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const g = useGate()
  const res = closeResult(g.outbox, session.id)
  const synced = res && ['APPLIED', 'DUPLICATE', 'HELD'].includes(res.status)
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="flex flex-col gap-2">
          <div className="font-mono text-[15px]">{session.code}</div>
          {!synced && <Banner tone="info">{t('gate.closeDay.waitingServer')}</Banner>}
          {synced && res?.balanced === true && (
            <Banner tone="success" title={t('gate.closeDay.serverBalanced')}>
              <CheckCircle aria-hidden="true" />
            </Banner>
          )}
          {synced && res?.balanced === false && (
            <Banner tone="error" title={t('gate.closeDay.serverUnbalanced')}>
              <span className="inline-flex items-center gap-1">
                <WarningOctagon aria-hidden="true" /> {t('gate.open.blocked')}
              </span>
            </Banner>
          )}
          {synced && res?.balanced == null && (
            <Banner tone="info">{t('gate.closeDay.allSent')}</Banner>
          )}
        </CardBody>
      </Card>
      <Button size="touch" variant="secondary" onClick={() => navigate('/m/gate')}>
        {t('gate.day.title')}
      </Button>
    </div>
  )
}
